import { Divider, Flex, NumberFormat, SkeletonBox, Space, Spacing } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getCreditNoteDepositSettlement } from '@features/invoices/utils/depositInvoices/getCreditNoteDepositSettlement'
import { getInvoiceSummaryTotals } from '@features/invoices/utils/getInvoiceSummaryTotals'
import { InvoiceDiscountCalculator } from '@features/invoices/utils/invoiceDiscountCalculator'
import { isInvoiceFormRowLine } from '@features/invoices/utils/isInvoiceFormRowLine'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NumberValue } from '../../../../../../../../../../utils'
import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { useCreditNoteSourceInvoice } from '../../../../../../hooks/useCreditNoteSourceInvoice'
import { useInvoiceEditorBaseCurrency } from '../../../../../../hooks/useInvoiceEditorBaseCurrency'
import { useInvoiceEditorBillingStage } from '../../../../../../hooks/useInvoiceEditorBillingStage'
import { useInvoiceEditorDocumentType } from '../../../../../../hooks/useInvoiceEditorDocumentType'
import { AmountDueSummary } from './elements/AmountDueSummary'
import { DepositDeduction } from './elements/DepositDeduction'
import { InvoiceGlobalDiscount } from './elements/InvoiceGlobalDiscount'
import { InvoiceSummaryExchangeRate } from './elements/InvoiceSummaryExchangeRate'
import { InvoiceSummaryLine } from './elements/InvoiceSummaryLine'
import { InvoiceSummaryTaxes } from './elements/InvoiceSummaryTaxes'
import { InvoiceSummaryTotal } from './elements/InvoiceSummaryTotal'
import { useFinalInvoiceDepositSettlement } from './hooks/useFinalInvoiceDepositSettlement'
import * as Styled from './styles'

export const InvoiceSummary = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { getInvoiceLocale } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)
  const { discountMode, invoiceEditorForm } = useInvoiceEditorForm()
  const { baseCurrency, isLoading } = useInvoiceEditorBaseCurrency()
  const { isCreditNote } = useInvoiceEditorDocumentType()
  const billingStage = useInvoiceEditorBillingStage()
  const creditNoteSourceInvoice = useCreditNoteSourceInvoice()

  const locale = getInvoiceLocale()
  const { watch } = invoiceEditorForm
  const currency = watch('currency')
  const discount = watch('discount')
  const discountType = watch('discountType')
  const exchangeRate = watch('exchangeRate')
  const invoiceLines = watch('invoiceLines')
  const originalInvoiceTotalIncludingVat = watch('originalInvoiceTotalIncludingVat')

  const shouldDisplayLineDiscountSummary = discountMode === InvoiceDiscountMode.ByItem
  const shouldDisplayGlobalDiscountSummary = discountMode === InvoiceDiscountMode.Global
  // Deposit lines never carry a discount - a deposit invoice has no row lines a discount could apply to.
  const hasRowLines = invoiceLines.some(isInvoiceFormRowLine)
  const shouldDisplayDiscountSummary =
    (shouldDisplayLineDiscountSummary || shouldDisplayGlobalDiscountSummary) && hasRowLines

  // A credit note or a deposit/final invoice can still carry an inherited global discount that
  // must stay in the totals below (see getInvoiceSummaryTotals's globalDiscount), but the discount
  // is not user-editable in those variants - whether creating from a quote or editing a saved
  // draft - so it's only visible in the summary.
  const isDepositOrFinalInvoice = !!billingStage
  const shouldDisplayGlobalDiscountInput =
    shouldDisplayGlobalDiscountSummary && !isCreditNote && !isDepositOrFinalInvoice

  const { discountTotal, grossTotal, netTotal, subTotal, taxSummary, taxTotal, validationIssues } =
    getInvoiceSummaryTotals({
      currency,
      discountType,
      globalDiscount: shouldDisplayGlobalDiscountSummary ? discount : undefined,
      invoiceLines,
      isCreditNote,
    })
  const { depositSettlement: finalInvoiceDepositSettlement, isLoading: isLoadingFinalInvoiceDepositSettlement } =
    useFinalInvoiceDepositSettlement({ grossTotal, netTotal, taxSummary, taxTotal })

  const creditNoteDepositSettlement = useMemo(
    () => getCreditNoteDepositSettlement(creditNoteSourceInvoice),
    [creditNoteSourceInvoice],
  )

  const depositSettlement = finalInvoiceDepositSettlement ?? creditNoteDepositSettlement
  const hasGlobalDiscountError = InvoiceDiscountCalculator.hasDiscountExceedsBaseIssue(validationIssues, -1)

  const shouldDisplayVatRelatedSummary = !organization?.isVatExempted
  // Hide the line VAT while the amount-due block is loading and once it can replace the lines.
  // A final invoice whose deposit could not be loaded keeps the line breakdown.
  const shouldDisplayLineTaxBreakdown =
    shouldDisplayVatRelatedSummary && !depositSettlement && !isLoadingFinalInvoiceDepositSettlement
  const shouldDisplayTotalVat = taxSummary.length > 1
  const shouldDisplayExchangeRate = exchangeRate && currency !== baseCurrency
  const shouldDisplayOriginalInvoiceTotal = !!originalInvoiceTotalIncludingVat

  const expectedAmount = useMemo(
    () => (exchangeRate ? new NumberValue(grossTotal).divide(exchangeRate).getRounded() : 0),
    [exchangeRate, grossTotal],
  )

  const getDisplayedCurrency = useCallback(
    (value: number, currency: string) => {
      return NumberFormat.getCurrencyNumber(value, currency, locale)
    },
    [locale],
  )

  if (isLoading || !baseCurrency) {
    return <SkeletonBox fullHeight fullWidth />
  }

  return (
    <Flex alignItems="flex-end" flexDirection="column">
      <Styled.InvoiceSummaryContent>
        {shouldDisplayOriginalInvoiceTotal && (
          <>
            <InvoiceSummaryLine
              label={t('external_invoices.editor.summary.initial_amount_to_refund')}
              text={getDisplayedCurrency(originalInvoiceTotalIncludingVat, currency)}
            />
            <Space size="s8" />
          </>
        )}
        {shouldDisplayGlobalDiscountInput && <InvoiceGlobalDiscount hasDiscountError={hasGlobalDiscountError} />}
        {shouldDisplayDiscountSummary && (
          <>
            <Flex flexDirection="column" gap={Spacing.S8}>
              <InvoiceSummaryLine
                label={t('external_invoices.editor.summary.subtotal_excluding_vat')}
                text={getDisplayedCurrency(subTotal, currency)}
              />
              <InvoiceSummaryLine
                label={t('external_invoices.editor.summary.discount_amount')}
                text={getDisplayedCurrency(discountTotal, currency)}
              />
              <Divider />
            </Flex>
            <Space size="s8" />
          </>
        )}
        {shouldDisplayVatRelatedSummary && (
          <>
            <InvoiceSummaryLine
              label={t('external_invoices.editor.summary.total_excluding_vat')}
              text={getDisplayedCurrency(netTotal, currency)}
            />
            <Space size="s8" />
            {shouldDisplayLineTaxBreakdown && (
              <>
                <InvoiceSummaryTaxes currency={currency} locale={locale} taxSummary={taxSummary} />
                {shouldDisplayTotalVat && (
                  <InvoiceSummaryLine
                    label={t('external_invoices.editor.summary.total_vat')}
                    text={getDisplayedCurrency(taxTotal, currency)}
                  />
                )}
              </>
            )}
            <Space />
          </>
        )}
        <InvoiceSummaryTotal
          label={t('external_invoices.editor.summary.total')}
          text={getDisplayedCurrency(grossTotal, currency)}
        />
        {isLoadingFinalInvoiceDepositSettlement && (
          <>
            <Space />
            <SkeletonBox fullWidth />
          </>
        )}
        {depositSettlement && (
          <>
            <Space />
            <DepositDeduction
              currency={currency}
              depositGrossTotal={depositSettlement.depositGrossTotal}
              locale={locale}
            />
            <AmountDueSummary
              amountDue={depositSettlement.amountDue}
              currency={currency}
              locale={locale}
              shouldShowTaxBreakdown={shouldDisplayVatRelatedSummary}
            />
          </>
        )}
        {shouldDisplayExchangeRate && (
          <>
            <Space />
            <InvoiceSummaryExchangeRate amountInBaseCurrency={getDisplayedCurrency(expectedAmount, baseCurrency)} />
          </>
        )}
      </Styled.InvoiceSummaryContent>
    </Flex>
  )
}
